import {
  StorefrontCountryIcon,
  StorefrontLocale,
  useL10n,
  useStorefronts
} from '@sfstudios/shapeshifter';
import { themeColor } from '../../theme';
import React from 'react';
import styled from 'styled-components/macro';
import { useIsCursorVisible } from '../../context/MouseContext';
import { useHover } from '../../hooks/useHover';
import { useKeyListener } from '../../hooks/useKeyListener';
import {
  useColumnNavigation,
  useRowNavigation
} from '../../hooks/useKeyNavigation';
import { useRemoteControllerFocus } from '../../hooks/useRemoteControllerFocus';
import { fontSize, lineHeight, whiteSpace } from '../../theme';
import { ButtonProps } from '../../types';
import { saveLocale } from '../../utils/deviceInfo';
import { Platform } from '../../utils/platform';
import { MinimalButton, UnstyledHTMLButton } from '../Button';
import { Focusable } from '../Focusable';
import { Icon } from '../Icon';
import { Modal } from './Modal';

const ModalContent = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${themeColor('ui900')};
  display: flex;
  align-items: center;
  justify-content: center;
  ul {
    display: flex;
    margin-bottom: ${whiteSpace['10']};
  }
  > div {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    max-width: 55%;
  }
`;

const ModalTitle = styled.h2`
  height: 88px; // in case translation isn't immediately available
  margin: 0 0 ${whiteSpace['14']} 0;
  font-size: ${fontSize['3xl']};
  color: ${themeColor('ui200')};
  line-height: ${lineHeight.snug};
  text-align: center;
`;

const CountryRowStyles = styled.li<{ focused: boolean }>`
  outline: ${(props) => (props.focused ? '4px solid white' : 'none')};
  transform: scale(${(props) => (props.focused ? '1.1' : 1)});
  ${() => (Platform.isLowEnd() ? '' : 'transition: transform 0.125s;')}
  margin-right: ${whiteSpace['8']};
  button {
    vertical-align: bottom;
    line-height: 0;
  }
`;

const CountryRow = ({
  icon,
  focused,
  locale
}: {
  icon: StorefrontCountryIcon;
  locale: StorefrontLocale;
  focused: boolean;
}) => {
  const [hoverRef, isHovered] = useHover();
  const isCursorVisible = useIsCursorVisible();
  const isFocused = isCursorVisible ? isHovered : focused;
  return (
    <CountryRowStyles focused={isFocused}>
      <Focusable<ButtonProps>
        as={UnstyledHTMLButton}
        focused={isFocused}
        aria-label={locale}
        onClick={() => {
          saveLocale(locale);
          window.location.reload();
        }}
      >
        <div ref={hoverRef}>
          <Icon name={icon} width={200} height={150} />
        </div>
      </Focusable>
    </CountryRowStyles>
  );
};

const CountryPickerModal: React.FC<{
  close?: () => void;
}> = ({ close }) => {
  const nRows = close ? 2 : 1;
  const focusedRow = useRowNavigation(nRows);
  const isColumnNavActive = !close || focusedRow === 0;
  const focusedCol = useColumnNavigation(4, isColumnNavActive);
  const hasColOrRow = useRemoteControllerFocus();
  const isCursorVisible = useIsCursorVisible();
  const [ref, isHovered] = useHover();

  const { t } = useL10n();
  const isBooting = false;
  const { items, titleKey, active } = useStorefronts();

  useKeyListener({
    back: () => {
      close && close();
    }
  });

  return (
    <Modal>
      <ModalContent>
        {!isBooting && (
          <div>
            <ModalTitle>{t(titleKey)}</ModalTitle>
            <ul>
              {items.map((item, index) => {
                const isNavFocused =
                  !isCursorVisible &&
                  hasColOrRow(0, focusedRow) &&
                  hasColOrRow(index, focusedCol);
                const isSelected = active.locale === item.locale;
                return (
                  <CountryRow
                    {...item}
                    key={index}
                    focused={isNavFocused || isSelected}
                  />
                );
              })}
            </ul>
            {close && (
              <Focusable<ButtonProps>
                as={UnstyledHTMLButton}
                onClick={close}
                focused={isCursorVisible ? isHovered : focusedRow === 1}
              >
                <div ref={ref}>
                  <MinimalButton width={400} iconStart="arrow-left">
                    {t('glossary.close_modal', { defaultValue: 'Close' })}
                  </MinimalButton>
                </div>
              </Focusable>
            )}
          </div>
        )}
      </ModalContent>
    </Modal>
  );
};

export default CountryPickerModal;
